import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { message } from 'antd';
import TransitManagementFilter, { TransitManagementFilterOptions } from './components/TransitManagementFilter';
import TransitManagementTable from './components/TransitManagementTable';
import AddRouteModal, { AddRouteFormData } from './components/AddRouteModal';
import { GET_HUB_LIST, GetHubListResponse } from './graphql/queries/getHubList';
import { GET_TMS_TRANSIT_ROUTES, GetTmsTransitRoutesResponse, TmsTransitRouteFiltersInput } from './graphql/queries/getTmsTransitRoutes';
import { CREATE_TMS_TRANSIT_ROUTE, CreateTmsTransitRouteResponse, TmsTransitRouteInput } from './graphql/mutations/createTmsTransitRoute';
import { UPDATE_TMS_TRANSIT_ROUTE_STATUS, UpdateTmsTransitRouteStatusResponse } from './graphql/mutations/updateTmsTransitRouteStatus';
import { formatTimeRangeToISO } from '../../../utils/dateTimeUtils';
import { useTranslation } from 'react-i18next';
import TablePaginationBar from '../../../components/TablePaginationBar';

const TransitManagementPage: React.FC = () => {
    const { t } = useTranslation();
    // 筛选状态
    const [filters, setFilters] = useState<TransitManagementFilterOptions>({
        originHub: '',
        destinationHub: '',
        routeStatus: '',
        updateTime: null
    });

    // 分页状态
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10
    });
    
    // 添加路线对话框状态
    const [addModalVisible, setAddModalVisible] = useState(false);

    // GraphQL查询 - 获取Hub列表
    const { data: hubData, loading: hubsLoading } = useQuery<GetHubListResponse>(GET_HUB_LIST, {
        fetchPolicy: 'cache-and-network'
    });

    // 构建查询过滤条件
    const queryFilters: TmsTransitRouteFiltersInput = useMemo(() => {
        // 处理时间范围筛选
        const timeRange = formatTimeRangeToISO(filters.updateTime);
        
        return {
            originHubId: filters.originHub || undefined,
            destinationHubId: filters.destinationHub || undefined,
            status: filters.routeStatus ? parseInt(filters.routeStatus) : undefined,
            updatedStart: timeRange?.start || undefined,
            updatedEnd: timeRange?.end || undefined
        };
    }, [filters]);

    // 通用查询函数
    const executeQuery = (customFilters?: TmsTransitRouteFiltersInput, customPagination?: { page: number; size: number }) => {
        refetch({
            filters: customFilters || queryFilters,
            pageRequest: customPagination || {
                page: pagination.current || 1,
                size: pagination.pageSize || 10
            }
        });
    };

    // GraphQL查询 - 获取转运路线列表
    const { data: routeData, loading, refetch, error } = useQuery<GetTmsTransitRoutesResponse>(
        GET_TMS_TRANSIT_ROUTES,
        {
            variables: {
                filters: {},
                pageRequest: { page: 1, size: 10 }
            }
        }
    );

    // GraphQL变更 - 创建转运路线
    const [createTmsTransitRoute, { loading: addLoading }] = useMutation<
        CreateTmsTransitRouteResponse,
        { input: TmsTransitRouteInput }
    >(CREATE_TMS_TRANSIT_ROUTE);

    // GraphQL变更 - 更新转运路线状态
    const [updateTmsTransitRouteStatus] = useMutation<
        UpdateTmsTransitRouteStatusResponse,
        { id: string; status: number }
    >(UPDATE_TMS_TRANSIT_ROUTE_STATUS, {
        onCompleted: (data) => {
            if (data.updateTmsTransitRouteStatus) {
                message.success('Operation successful');
                refetch();
            } else {
                message.error('Operation failed');
            }
        },
        onError: (error) => {
            message.error(`Operation failed: ${error.message}`);
        }
    });

    // 筛选处理
    const handleFilter = () => {
        setPagination(prev => ({ ...prev, current: 1 }));
        executeQuery(queryFilters, { page: 1, size: pagination.pageSize || 10 });
    };

    // 重置筛选
    const handleResetFilters = () => {
        setFilters({
            originHub: '',
            destinationHub: '',
            routeStatus: '',
            updateTime: null
        });
        setPagination(prev => ({ ...prev, current: 1 }));
        executeQuery({}, { page: 1, size: pagination.pageSize || 10 });
    };

    // 禁用路线
    const handleDisable = (id: string) => {
        updateTmsTransitRouteStatus({
            variables: { id, status: 0 }
        });
    };

    // 启用路线
    const handleEnable = (id: string) => {
        updateTmsTransitRouteStatus({
            variables: { id, status: 1 }
        });
    };

    // 添加新路线
    const handleAddRoute = () => {
        setAddModalVisible(true);
    };

    // 处理添加路线
    const handleAddRouteSubmit = async (formData: AddRouteFormData): Promise<void> => {
        return new Promise((resolve, reject) => {
            createTmsTransitRoute({
                variables: {
                    input: {
                        originHubId: formData.originHub,
                        destinationHubId: formData.destinationHub,
                        status: 1
                    }
                },
                onCompleted: (data) => {
                    message.success('Route added successfully');
                    setAddModalVisible(false);
                    refetch();
                    resolve();
                },
                onError: (error) => {
                    reject(error);
                }
            });
        });
    };

    // 取消添加路线
    const handleAddRouteCancel = () => {
        setAddModalVisible(false);
    };

    // 处理表格分页变化
    const handleTableChange = (pagination: any) => {
        setPagination({
            current: pagination.current,
            pageSize: pagination.pageSize
        });
        executeQuery(queryFilters, { page: pagination.current, size: pagination.pageSize });
    };

    // 转换数据格式
    const hubs = hubData?.hubList?.filter(hub => hub.siteType === 1) || [];
    
    // 创建Hub ID到sortingCode的映射，提高查找性能
    const hubSortingCodeMap = useMemo(() => {
        const map = new Map<string, string>();
        hubs.forEach(hub => {
            map.set(hub.id, hub.sortingCode || hub.name);
        });
        return map;
    }, [hubs]);
    
    const actualRouteData = routeData?.getTmsTransitRoutes?.content || [];
    const data = actualRouteData.map(route => ({
        id: route.id,
        originHub: hubSortingCodeMap.get(route.originHubId)!!,
        destinationHub: hubSortingCodeMap.get(route.destinationHubId)!!,
        isValid: route.status === 1,
        updatedAt: route.updatedAt
    }));

    const totalElements = routeData?.getTmsTransitRoutes?.totalElements || 0;

    return (
            <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
                    {/* 筛选区域 */}
                    <TransitManagementFilter
                        filters={filters}
                        setFilters={setFilters}
                        onFilter={handleFilter}
                        onResetFilters={handleResetFilters}
                        onAddRoute={handleAddRoute}
                        hubs={hubs}
                        hubsLoading={hubsLoading}
                    />

                    {/* 表格区域 */}
                    <TransitManagementTable
                        data={data}
                        loading={loading}
                        onDisable={handleDisable}
                        onEnable={handleEnable}
                        onTableChange={handleTableChange}
                    />

                    {/* 分页 */}
                    <TablePaginationBar
                        total={totalElements}
                        current={pagination.current}
                        pageSize={pagination.pageSize}
                        onChange={(page, size) => handleTableChange({ current: page, pageSize: size })}
                    />

                {/* 添加路线对话框 */}
                <AddRouteModal
                    visible={addModalVisible}
                    onCancel={handleAddRouteCancel}
                    onOk={handleAddRouteSubmit}
                    loading={addLoading}
                    hubs={hubs}
                />
            </div>
    );
};

export default TransitManagementPage;
